import type { Theme } from '@mui/material/styles';

/** MUI theme palette names accepted for chip-variant `Select` backgrounds. */
export type SelectChipPaletteColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'default';

/** Theme palette token for chip-variant `Select` backgrounds (`variant="chip"`). */
export type SelectChipBackgroundColor = SelectChipPaletteColor;

type PaletteKey = Exclude<SelectChipPaletteColor, 'default'>;

const DEFAULT_CHIP_BG = '#F2F4F9';
const DEFAULT_CHIP_FG = '#1E2A3A';

/** Chip backgrounds use theme `main`, then `light` (no A100 tints). */
const CHIP_PALETTE_SHADES = ['main', 'light'] as const;

type Rgb = { r: number; g: number; b: number };

function parseHexColor(hex: string): (Rgb & { a: number }) | null {
  const normalized = hex.replace('#', '').trim();
  if (normalized.length === 3) {
    const r = parseInt(normalized[0] + normalized[0], 16);
    const g = parseInt(normalized[1] + normalized[1], 16);
    const b = parseInt(normalized[2] + normalized[2], 16);
    return { r, g, b, a: 1 };
  }
  if (normalized.length === 6 || normalized.length === 8) {
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    const a = normalized.length === 8 ? parseInt(normalized.slice(6, 8), 16) / 255 : 1;
    if ([r, g, b, a].some(Number.isNaN)) return null;
    return { r, g, b, a };
  }
  return null;
}

function parseCssColor(color: string): (Rgb & { a: number }) | null {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    return parseHexColor(trimmed);
  }

  const rgbMatch = trimmed.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i);
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    const a = rgbMatch[4] !== undefined ? Number(rgbMatch[4]) : 1;
    if ([r, g, b, a].some(Number.isNaN)) return null;
    return { r, g, b, a };
  }

  return null;
}

/** Blends a translucent color on white for luminance checks. */
function toOpaqueRgb(color: Rgb & { a: number }): Rgb {
  if (color.a >= 1) {
    return { r: color.r, g: color.g, b: color.b };
  }
  return {
    r: Math.round(color.r * color.a + 255 * (1 - color.a)),
    g: Math.round(color.g * color.a + 255 * (1 - color.a)),
    b: Math.round(color.b * color.a + 255 * (1 - color.a))
  };
}

function getRelativeLuminance({ r, g, b }: Rgb): number {
  const toLinear = (channel: number) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const rs = toLinear(r);
  const gs = toLinear(g);
  const bs = toLinear(b);
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** Picks light or dark foreground text for a background color. */
export function getContrastTextForBackground(background: string, theme: Theme): string {
  const parsed = parseCssColor(background);
  const darkText = theme.palette.text?.primary ?? DEFAULT_CHIP_FG;
  const lightText = theme.palette.common?.white ?? '#ffffff';

  if (!parsed) {
    return darkText;
  }

  const luminance = getRelativeLuminance(toOpaqueRgb(parsed));
  return luminance > 0.55 ? darkText : lightText;
}

function resolveChipPaletteShade(paletteEntry: Record<string, unknown>): string | undefined {
  for (const shade of CHIP_PALETTE_SHADES) {
    const value = paletteEntry[shade];
    if (value != null && value !== '') {
      return String(value);
    }
  }
  return undefined;
}

/** Resolves a palette token to a concrete CSS color via the active MUI theme. */
export function resolveSelectChipBackground(color: SelectChipBackgroundColor | undefined, theme: Theme): string {
  if (!color || color === 'default') {
    return theme.palette.grey?.[100] ?? DEFAULT_CHIP_BG;
  }

  const paletteEntry = theme.palette[color as PaletteKey];
  if (!paletteEntry || typeof paletteEntry !== 'object') {
    return DEFAULT_CHIP_BG;
  }

  return resolveChipPaletteShade(paletteEntry as unknown as Record<string, unknown>) ?? DEFAULT_CHIP_BG;
}

export function resolveSelectChipColors(
  color: SelectChipBackgroundColor | undefined,
  theme: Theme
): { background: string; foreground: string } {
  const background = resolveSelectChipBackground(color, theme);
  return {
    background,
    foreground: getContrastTextForBackground(background, theme)
  };
}
